import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  client: "",
  amount: "$0.00",
  status: "In Progress",
};

export default function OrderFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initial,
  clientOptions = [],
}) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(initial || {}) });
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const valid = form.client.trim().length > 0 && form.amount.trim().length > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="order-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {initial ? `Edit Order ${initial.number}` : "New Order"}
          </DialogTitle>
          <DialogDescription>
            Demo order — stored in local state only.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="o-client" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Client</Label>
            <Select value={form.client} onValueChange={set("client")}>
              <SelectTrigger data-testid="order-form-client" className="h-10 rounded-lg">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clientOptions.length === 0 && (
                  <SelectItem value="none" disabled>No clients</SelectItem>
                )}
                {clientOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="o-amount" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Amount</Label>
              <Input id="o-amount" data-testid="order-form-amount" value={form.amount} onChange={set("amount")} placeholder="$1,200.00" className="h-10 rounded-lg" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-status" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="order-form-status" className="h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Pending Payment">Pending Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="order-form-cancel" className="rounded-lg">
              Cancel
            </Button>
            <Button type="submit" disabled={!valid} data-testid="order-form-submit" className="bg-primary hover:bg-primary/90 rounded-lg">
              {initial ? "Save Changes" : "Create Order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
